import { Row, Col } from 'antd'
import AntInput from 'antd/lib/input'
import AntSelect from 'antd/lib/select'
import lodashForEach from 'lodash/forEach'
import lodashPickBy from 'lodash/pickBy'
import { useState, useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import styled from 'styled-components'

import { getFormattedDate } from '../../clients/tools'
import { AuthLayout } from '../../components/AuthLayout'
import { CheckinConversation } from '../../components/CheckinConversation'
import { CloudinaryUploader } from '../../components/CloudinaryUploader'
import KanbanBoard from '../../components/KanbanBoard'
import { PageBody } from '../../components/PageBody'
import { Select } from '../../components/Select'
import { useDriverCheckins } from '../../hooks/useCheckins'
import { useModal } from '../../hooks/useModal'
import type { UpdateCheckinData } from '../../models/client/checkins'
import { selectSiteDirectory } from '../../store/sites'
import { selectCompanyId } from '../../store/user'
import { isEqual } from '../../util/isEqual'

const Container = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-flow: column nowrap;
`

const CardDataContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-flow: column nowrap;
  padding: 10px;
  min-height: 400px;
`

const InputContainer = styled.div`
  padding: 5px;
`

const SelectContainer = styled.div`
  max-width: 620px;
`

const SelectSiteContainer = styled.div`
  width: 300px;
  float: left;
  margin-right: 5px;
  margin-bottom: 5px;
`

const CloudinaryUploaderContainer = styled.div`
  max-width: 300px;
  max-height: 300px;
`

const SelectLocationContainer = styled(SelectSiteContainer)``

const StyledPageBody = styled(PageBody)`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
`

const Label = styled.span`
  font-weight: bold;
`

export function CheckIn(): JSX.Element {
  let cardData: UpdateCheckinData = {}

  const locations = {}
  const companyId = useSelector(selectCompanyId, shallowEqual)

  const [activeCard, setCurrentActiveCard] = useState(null)
  const [selectedSite, setSelectedSite] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [canAssignDock, setDockAssignFlag] = useState(false)
  const [isDragged, setIsDragged] = useState(false)
  const [targetStatus, setTargetStatus] = useState('')
  const [usedDock, setUsedDock] = useState([])
  const [locationType, setLocationType] = useState('warehouse')

  let sites = useSelector(selectSiteDirectory, isEqual)

  sites = lodashPickBy(sites, (v) => {
    return v.companyId === companyId
  })
  lodashForEach(sites, (s) => {
    lodashForEach(s.locations, (l) => {
      locations[l.id] = l
    })
  })

  const [Modal, triggerModal] = useModal({
    onCancel: () => {
      cardData.viewedAt = new Date().toISOString()
      updateCheckinData(activeCard.id, cardData)
      setRefresh(!refresh)
      setDockAssignFlag(false)
      setIsDragged(false)
      setActiveCard(null)
      resetCardData()
    },
    onConfirm: () => {
      if (isDragged || cardData) {
        let currentTargetStatus = targetStatus
        if (cardData.dockId || isDragged) {
          if (cardData.dockId && locationType === 'checkout_only') {
            currentTargetStatus = 'Outgated'
          }
          if (currentTargetStatus === 'Outgated' && !activeCard.outgatedAt) {
            cardData.outgatedAt = new Date().toISOString()
          }
          cardData.statusId = checkinStatus.find((val) => val.name === currentTargetStatus)?.id
        }
        if (currentTargetStatus === 'Outgated' && locationType !== 'checkout_only') {
          cardData.dockId = null
        }

        if (cardData.dockId && locationType === 'checkout_only') {
          const dockName = dockOptions[cardData.dockId] as string
          cardData.customMessage = `You have been assigned to dock ${dockName}. Please proceed to Entrance and let the agent know of your arrival. If you do not arrive within 10 minutes you may lose your spot.`
        }

        cardData.viewedAt = new Date().toISOString()
        updateCheckinData(activeCard.id, cardData)
      }
      setDockAssignFlag(false)
      setIsDragged(false)
      resetCardData()
    },
  })

  const {
    initialBoardData,
    isLoading,
    dockOptions,
    boardData,
    setBoardData,
    checkinStatus,
    fuelTypes,
    updateCheckinData,
    refresh,
    setRefresh,
    siteId,
    setSiteId,
    locationId,
    setLocationId,
  } = useDriverCheckins(locationType || 'warehouse')

  useEffect(() => {
    if (!isLoading) {
      setBoardData(initialBoardData)
    }
  }, [initialBoardData, isLoading, setBoardData])

  const resetCardData = (): void => {
    cardData = {}
  }

  const setActiveDocks = (): void => {
    const dockLaneId = checkinStatus.filter((val) => val.key === 'docking')[0].id
    const docked = []

    boardData.lanes.forEach((lane) => {
      if (lane.id === dockLaneId) {
        lane.cards.forEach((card) => {
          docked.push(card.dockId)
        })
      }
    })
    setUsedDock(docked)
  }

  const setActiveCard = (card): void => {
    setCurrentActiveCard(card)
  }

  const onCardClick = (cardId, _, laneId): void => {
    resetCardData()
    checkinStatus.forEach((val) => {
      if (val.name === 'Ingated' && val.id === laneId) {
        setDockAssignFlag(true)
        setTargetStatus('Docking')
      }
      if (val.name === 'Docking' && val.id === laneId && locationType === 'yard') {
        setDockAssignFlag(true)
      }
    })
    for (let i = 0; i < boardData.lanes.length; i++) {
      if (boardData.lanes[i].id === laneId) {
        for (let j = 0; j < boardData.lanes[i].cards.length; j++) {
          if (boardData.lanes[i].cards[j].id === cardId) {
            setActiveCard(boardData.lanes[i].cards[j])
            break
          }
        }
        break
      }
    }
    setActiveDocks()
    triggerModal(true)
  }

  const handleDragEnd = (cardId, sourceLaneId, targetLaneId, position, cardDetails): void => {
    setActiveDocks()
    // Check if user is dragging card back or moving inside the same lane
    if (targetLaneId === sourceLaneId) {
      setRefresh(!refresh)
    } else {
      setIsDragged(true)
      resetCardData()
      let isFromIngated = false
      let isToOutgated = false
      let isToDocking = false
      checkinStatus.forEach((val) => {
        if (val.key === 'ingated' && val.id === sourceLaneId) {
          isFromIngated = true
          setDockAssignFlag(true)
        }
        if (val.key === 'outgated' && val.id === targetLaneId) {
          isToOutgated = true
          setDockAssignFlag(true)
        }
        if (val.key === 'docking' && val.id === targetLaneId) {
          isToDocking = true
          setDockAssignFlag(true)
        }
      })

      if (isToDocking) {
        setTargetStatus('Docking')
        setActiveCard(cardDetails)
        triggerModal(true)
      } else if (isToOutgated) {
        setTargetStatus('Outgated')
        setActiveCard(cardDetails)
        triggerModal(true)
      } else if (!isFromIngated && !isToOutgated && !isToDocking) {
        setTargetStatus('Ingated')
        setActiveCard(cardDetails)
        triggerModal(true)
      }
    }
  }

  const onCardInputChange = (input): void => {
    if (input.target.name === 'assetLength') {
      const meta = JSON.parse(JSON.stringify(activeCard.meta))
      meta.assetLength = input.target.value
      cardData.meta = meta
    } else {
      cardData[input.target.name] = input.target.value
    }
  }

  const onUploadCompleted = (image): void => {
    const meta = JSON.parse(JSON.stringify(activeCard.meta))
    meta.files.push(image)
    cardData.meta = meta
  }

  const getDockAlias = (): string => {
    if (selectedLocation && selectedLocation.meta?.dockAlias) {
      return selectedLocation.meta?.dockAlias
    }
    return 'Dock'
  }

  return (
    <AuthLayout>
      <StyledPageBody>
        <Container>
          <Modal
            confirmLabel={canAssignDock ? 'Confirm' : 'Ok'}
            cancelLabel="Cancel"
            widthPx={860}
            key="kanban-card-modal"
          >
            <Row key="kanban-card-modal-row">
              <Col span={12}>
                <CardDataContainer>
                  <InputContainer>
                    <Label>Driver: </Label>
                    <Row>
                      <Col span={12}>
                        <AntInput
                          defaultValue={activeCard?.firstName}
                          name="firstName"
                          allowClear={false}
                          type="text"
                          onBlur={onCardInputChange}
                        />
                      </Col>
                      <Col span={12}>
                        <AntInput
                          defaultValue={activeCard?.lastName}
                          name="lastName"
                          allowClear={false}
                          type="text"
                          onBlur={onCardInputChange}
                        />
                      </Col>
                    </Row>
                  </InputContainer>
                  <InputContainer>
                    <Label className="bold">Phone: </Label>
                    <AntInput
                      defaultValue={activeCard?.phoneNumber}
                      name="phoneNumber"
                      allowClear={false}
                      type="text"
                      disabled
                      onBlur={onCardInputChange}
                    />
                  </InputContainer>
                  <InputContainer>
                    <Label>
                      {locationType !== 'employee' ? 'Carrier Name' : 'Employee Number'}
                    </Label>
                    <AntInput
                      defaultValue={activeCard?.carrierName}
                      name="carrierName"
                      allowClear={false}
                      type="text"
                      onBlur={onCardInputChange}
                    />
                  </InputContainer>
                  {locationType !== 'employee' && (
                    <div>
                      <InputContainer>
                        <Label>
                          {locationType === 'checkout_only'
                            ? 'Destination:'
                            : 'Customer Name (Hauling For):'}
                        </Label>
                        <AntInput
                          defaultValue={activeCard?.customerName}
                          name="customerName"
                          allowClear={false}
                          type="text"
                          onBlur={onCardInputChange}
                        />
                      </InputContainer>
                      <InputContainer>
                        <Label>Booking/PO/BOL: </Label>
                        <AntInput
                          defaultValue={activeCard?.loadNumber}
                          name="loadNumber"
                          allowClear={false}
                          type="text"
                          onBlur={onCardInputChange}
                        />
                      </InputContainer>
                      <InputContainer>
                        <Label>Container/Trailer Number: </Label>
                        <AntInput
                          defaultValue={activeCard?.assetNumber}
                          name="assetNumber"
                          allowClear={false}
                          type="text"
                          onBlur={onCardInputChange}
                        />
                      </InputContainer>
                      {locationType === 'checkout_only' && (
                        <div>
                          <InputContainer>
                            <Label>Trailer Length: </Label>
                            <AntInput
                              defaultValue={activeCard?.meta?.assetLength}
                              name="assetLength"
                              allowClear={false}
                              type="text"
                              onBlur={onCardInputChange}
                            />
                          </InputContainer>
                        </div>
                      )}
                      {locationType !== 'checkout_only' && (
                        <div>
                          <InputContainer>
                            <Label>Chassis Number: </Label>
                            <AntInput
                              defaultValue={activeCard?.chassisNumber}
                              name="chassisNumber"
                              allowClear={false}
                              type="text"
                              onBlur={onCardInputChange}
                            />
                          </InputContainer>
                          <InputContainer>
                            <Label>Security Seal: </Label>
                            <AntInput
                              defaultValue={activeCard?.securitySeal}
                              name="securitySeal"
                              allowClear={false}
                              type="text"
                              onBlur={onCardInputChange}
                            />
                          </InputContainer>
                          {fuelTypes && (
                            <InputContainer>
                              <Label>Fuel Type: </Label>
                              <AntSelect
                                placeholder=""
                                options={Object.keys(fuelTypes).map((val) => {
                                  return {
                                    label: fuelTypes[val],
                                    value: val,
                                  }
                                })}
                                defaultValue={activeCard?.fuelTypeId}
                                onSelect={(_, option) => {
                                  onCardInputChange({
                                    target: {
                                      value: option.value,
                                      name: 'fuelTypeId',
                                    },
                                  })
                                }}
                              />
                            </InputContainer>
                          )}
                        </div>
                      )}

                      <InputContainer>
                        <Label>{locationType === 'yard' ? 'Zone' : getDockAlias()}:</Label>
                        {activeCard?.dockId ? dockOptions[activeCard?.dockId] : 'Not Set'}
                      </InputContainer>
                      {canAssignDock && (
                        <InputContainer>
                          <Row>
                            <Col span={8}>
                              <Label>
                                Assign a{locationType === 'yard' ? ' Zone' : ` ${getDockAlias()}`}:
                              </Label>
                            </Col>
                            <Col span={12}>
                              <AntSelect
                                placeholder="None"
                                options={Object.keys(dockOptions)
                                  .filter((val) => {
                                    return !usedDock.includes(val)
                                  })
                                  .map((val) => {
                                    return {
                                      label: dockOptions[val],
                                      value: val,
                                    }
                                  })
                                  .sort((a, b) => parseInt(a.label) - parseInt(b.label))}
                                onSelect={(_, option) => {
                                  onCardInputChange({
                                    target: {
                                      value: option.value,
                                      name: 'dockId',
                                    },
                                  })
                                }}
                              />
                            </Col>
                          </Row>
                        </InputContainer>
                      )}
                      <InputContainer>
                        <Label>Uploaded Files: </Label>
                        {activeCard?.meta?.files && activeCard?.meta?.files.length && (
                          <ul>
                            {activeCard?.meta?.files.map((item) => (
                              <li key={item.url}>
                                <a target="_blank" href={item.url} rel="noreferrer">
                                  {item.tags[0].replace('-', ' ').toUpperCase()}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                        {activeCard && (
                          <CloudinaryUploaderContainer>
                            <CloudinaryUploader
                              cloudName="navtrac"
                              uploadPreset="pkoz2ikr"
                              folder={activeCard?.siteId}
                              buttonCaption="Upload Security Seal"
                              tags={[
                                'security-seal',
                                activeCard?.loadNumber,
                                activeCard?.assetNumber,
                              ]}
                              onUploadSuccess={(image) => {
                                onUploadCompleted(image)
                              }}
                            />
                          </CloudinaryUploaderContainer>
                        )}
                      </InputContainer>
                      <InputContainer>
                        <Label>Entry Created: </Label>
                        {getFormattedDate(activeCard?.createdAt)}
                      </InputContainer>
                    </div>
                  )}
                </CardDataContainer>
              </Col>
              <Col span={12}>
                {activeCard?.conversation && (
                  <CheckinConversation conversation={activeCard?.conversation} />
                )}
              </Col>
            </Row>
          </Modal>
          <SelectContainer>
            <SelectSiteContainer>
              <Select
                className="check-in-site-select"
                placeholder="Select a Site"
                options={Object.keys(sites).map((val) => {
                  return {
                    label: sites[val].name,
                    value: sites[val].id,
                  }
                })}
                value={siteId}
                onChange={(nextValue) => {
                  setSelectedSite(sites[nextValue])
                  setLocationType(
                    (sites[nextValue] && sites[nextValue].meta?.checkinType) || 'warehouse'
                  )
                  setSiteId(nextValue)
                }}
              />
            </SelectSiteContainer>

            {selectedSite && selectedSite.locations && selectedSite.locations.length > 1 && (
              <SelectLocationContainer>
                <Select
                  className="check-in-Location-select"
                  placeholder={`Select a ${
                    selectedSite.settings?.isMultiTenant ? 'Tenant' : 'Location'
                  }`}
                  options={selectedSite.locations.map((val) => {
                    return {
                      label: val.name,
                      value: val.id,
                    }
                  })}
                  value={locationId}
                  onChange={(nextValue) => {
                    setLocationType(
                      (locations[nextValue] && locations[nextValue].meta?.type) || 'warehouse'
                    )
                    setLocationId(nextValue)
                    setSelectedLocation(locations[nextValue])
                  }}
                />
              </SelectLocationContainer>
            )}
          </SelectContainer>
          {!isLoading ? (
            <KanbanBoard
              data={boardData}
              isLaneDraggable={false}
              onCardClick={locationType !== 'employee' ? onCardClick : () => {}}
              handleDragEnd={handleDragEnd}
            />
          ) : (
            <div>Loading</div>
          )}
        </Container>
      </StyledPageBody>
    </AuthLayout>
  )
}
